<template>
    <div class="c-recipe-products" id="ingredients">
        <div v-if="!isInitialized" v-html="placeholder"></div>

        <template v-else>
            <div class="c-recipe-products__header">
                <div class="c-recipe-products-header">
                    <div class="c-recipe-products-header__title">
                        {{ $t('RecipeProducts.title') }}
                    </div>

                    <div
                        class="c-recipe-products-header__portions"
                        :class="{'is-loading': isFetchingProducts}"
                    >
                        <span class="sc-label">{{ $t('RecipeProducts.portions') }}</span>

                        <div class="c-form__quantity" :class="{'is-disabled': isAddingAllToCart}">
                            <button type="button" class="c-btn c-btn--cart is-decrement" @click="setPortions(-1)">
                                <i class="ico ico-minus"></i>
                            </button>

                            <input type="text" class="c-form__input" v-model="portions" min="1" autocomplete="off" readonly :disabled="isAddingAllToCart" />

                            <button type="button" class="c-btn c-btn--cart is-increment" @click="setPortions(+1)">
                                <i class="ico ico-plus"></i>
                            </button>
                        </div>
                    </div>

                    <div class="c-recipe-products-header__price" v-if="mainIngredients.length">
                        <span class="sc-price-text">{{ priceText }}</span>
                        <span class="sc-price" v-html="formatPrice(finalPrice)"></span>
                    </div>
                </div>

                <div class="c-recipe-products__disclaimer" v-if="disclaimer">
                    <i class="ico ico-info-circle"></i>
                    <span>{{ disclaimer }}</span>
                </div>
            </div>

            <div class="c-recipe-products__body">
                <RecipeItem
                    v-for="ingredient of mainIngredients"
                    :key="ingredient.id"
                    :ingredient="ingredient"
                    :selectedAlternative="selectedAlternatives[ingredient.id]"
                    :isAddingAllToCart="isAddingAllToCart"
                    @selectAlternative="selectAlternative"
                />
            </div>

            <!-- cena a "Pridat vsetko" su nad "Pravdepodobne mate doma"; ked su vsetky produkty "Pravdepodobne mate doma", nezobrazuju sa (LNS-1820) -->
            <div class="c-recipe-products__footer" v-if="mainIngredients.length">
                <div class="c-recipe-products-footer">
                    <div class="c-recipe-products-footer__price">
                        <span class="sc-price-text">{{ priceText }}</span>
                        <span class="sc-price" v-html="formatPrice(finalPrice)"></span>
                    </div>

                    <div class="c-recipe-products-footer__cart">
                        <button
                            class="c-btn c-btn--cart"
                            :class="{ 'is-loading': isAddingAllToCart }"
                            type="submit"
                            @click.prevent="addEverythingToCart($event)"
                        >
                            <i class="ico ico-cart"></i>
                            <span>{{ $t('RecipeProducts.addEverythingToCart') }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="c-recipe-products__at-home" v-if="atHomeIngredients.length">
                <div class="c-recipe-products__at-home-head">
                    <div class="sc-title">{{ $t('RecipeProducts.atHomeTitle') }}</div>
                    <div class="sc-text">{{ $t('RecipeProducts.atHomeText') }}</div>
                </div>

                <div class="c-recipe-products__body">
                    <RecipeItem
                        v-for="ingredient of atHomeIngredients"
                        :key="ingredient.id"
                        :ingredient="ingredient"
                        :selectedAlternative="selectedAlternatives[ingredient.id]"
                        :isAddingAllToCart="isAddingAllToCart"
                        @selectAlternative="selectAlternative"
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import client from 'FrontendJsAssets/common/client'
import { CancelToken, isCancel } from "axios";
import { formatPrice } from 'FrontendJsAssets/common/format'
import RecipeItem from 'VueComponents/RecipeProducts/item'

export default {
    props: {
        recipeId: {
            type: [String, Number],
            required: true
        },
        placeholder: {
            type: String,
            default: '',
        },
        disclaimer: {
            type: String,
            default: '',
        },
    },
    components: {
        RecipeItem: RecipeItem
    },
    data() {
        return {
            isInitialized: false,
            currentPortions: null,
            portions: null,
            recipe: null,
            selectedAlternatives: {},
            isFetchingProducts: false,
            fetchTimeout: null,
            fetchCancelToken: null,
            isAddingAllToCart: false,
        }
    },
    methods: {
        formatPrice: formatPrice,
        setPortions(value) {
            if (this.isAddingAllToCart) return

            this.portions += value
        },
        selectAlternative(ingredientId, alternative) {
            this.$set(this.selectedAlternatives, ingredientId, alternative)
        },
        addEverythingToCart(event) {
            this.isAddingAllToCart = true

            const handleError = () => {
                this.isAddingAllToCart = false

                window.Eshop.tippyAlert(event.target, this.$t("RecipeProducts.addAllToCartError"), {
                    theme: 'error',
                    zIndex: '200'
                }, 2500)
            }

            client.post("/kupit-produkty-receptu", {
                    // "Pravdepodobne mate doma" sa do kosika nepridava (LNS-1820)
                    products: this.mainIngredients.map(ingredient => {
                        const alternative = this.selectedAlternatives[ingredient.id]
                        const product = alternative ? alternative : ingredient.product

                        return {id: product.id, quantity: product.quantity}
                    })
                }).then(response => {
                    if (!response.data.status) {
                        handleError()
                        return
                    }

                    this.isAddingAllToCart = false
                    // setTimeout(() => {
                    //     window.location = response.data.url
                    // }, 250)

                    window.Eshop.tippyAlert(event.target, this.$t("RecipeProducts.addAllToCartSuccess"), {
                        theme: 'success',
                        zIndex: '200'
                    }, 2500)

                    setTimeout(() => {
                        window.location.reload()
                    }, 250)
                }).catch(handleError)
        },
        async fetchProducts(data = {}, timeout = 250) {
            this.isFetchingProducts = true

            if (this.fetchTimeout) {
                clearTimeout(this.fetchTimeout)
            }

            if (this.fetchCancelToken) {
                this.fetchCancelToken.cancel()
            }

            this.fetchCancelToken = CancelToken.source()
            this.fetchTimeout = setTimeout(() => {
                client
                    .post("/produkty-receptu", {
                        ...{ recipeId: this.recipeId },
                        ...data
                    }, {
                        cancelToken: this.fetchCancelToken.token
                    })
                    .then((response) => {
                        if (!response.data.status) {
                            return
                        }

                        this.$set(this, 'recipe', response.data.recipe)
                        this.portions = response.data.portions
                        this.currentPortions = response.data.portions
                        this.isFetchingProducts = false
                        this.isInitialized = true
                    })
                    .catch((error) => {
                        if (!isCancel(error)) {
                            console.error(error)
                            this.isFetchingProducts = false
                        }
                    })
            }, timeout)
        },
    },
    async mounted() {
        await this.fetchProducts({}, 0)
    },
    computed: {
        finalPrice() {
            if (!this.recipe) {
                return 0
            }

            return this.mainIngredients.reduce((acc, ingredient) => {
                const alternative = this.selectedAlternatives[ingredient.id]
                const product = alternative ? alternative : ingredient.product

                return acc + product.final_price
            }, 0)
        },
        priceText() {
            if (this.currentPortions > 1 && this.currentPortions < 5) {
                return this.$t('RecipeProducts.price24', {
                    count: this.currentPortions || '0'
                })
            }

            if (this.currentPortions == 1) {
                return this.$t('RecipeProducts.price1', {
                    count: this.currentPortions || '0'
                })
            }

            return this.$t('RecipeProducts.price5', {
                count: this.currentPortions || '0'
            })
        },
        ingredientsWithProducts() {
            if (!this.recipe) return []

            return this.recipe.custom_recipe_ingredients.filter((item) => item.product)
        },
        mainIngredients() {
            return this.ingredientsWithProducts.filter((item) => !item.at_home)
        },
        atHomeIngredients() {
            return this.ingredientsWithProducts.filter((item) => item.at_home)
        },
    },
    watch: {
        portions(newValue, oldValue) {
            if (isNaN(parseInt(newValue)) || newValue <= 0) {
                newValue = 1
                this.portions = newValue
            }

            if (this.isInitialized && newValue !== oldValue && oldValue !== null) {
                this.fetchProducts({
                    portions: this.portions
                })
            }
        }
    }
}
</script>
